import { privateMediaTelemetry } from '../utils/privateMediaTelemetry';
/**
 * Shared Logger Utility
 *
 * Provides a consistent logging interface across all ReKindle projects.
 * Supports optional Sentry integration via dependency injection.
 *
 * Features:
 * - Environment-aware logging (dev vs prod)
 * - Log levels with filtering
 * - Structured logging with context/scope
 * - Optional Sentry integration (breadcrumbs, error capture)
 * - Performance timing helpers
 * - Console grouping
 * - Table logging
 *
 * NOTE: This file intentionally uses console.* methods directly as it IS the
 * logger implementation. All other files should use this logger instead of
 * console.* directly.
 */

export type LogLevel = 'debug' | 'info' | 'log' | 'warn' | 'error';

/**
 * Sentry integration interface
 * Projects provide their own Sentry SDK functions via this interface
 */
export interface SentryIntegration {
  captureException: (error: Error, context?: Record<string, unknown>) => void;
  captureMessage: (message: string, level: 'info' | 'warning' | 'error',
  ) => void;
  addBreadcrumb: (breadcrumb: {
    category: string;
    message: string;
    level: 'debug' | 'info' | 'warning' | 'error';
  }) => void;
  isConfigured: () => boolean;
}

export interface LoggerConfig {
  /** Enable/disable all logging */
  enabled: boolean;
  /** Minimum log level to display */
  minLevel: LogLevel;
  /** Prefix for all log messages (e.g., 'Admin', 'App') */
  prefix?: string;
  /** Show timestamp in logs */
  showTimestamp: boolean;
  /** Whether running in development mode */
  isDev: boolean;
  /** Optional Sentry integration */
  sentry?: SentryIntegration;
  /** Use emoji prefixes (default: true in dev) */
  useEmoji?: boolean;
  /** Optional callback invoked for every log message (useful for in-app log consoles) */
  onLog?: (level: string, prefix: string, args: unknown[]) => void;
}

/**
 * Log level priority (higher = more severe)
 */
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  log: 2,
  warn: 3,
  error: 4,
};

/**
 * Emoji prefixes for each log level
 */
const LOG_LEVEL_EMOJI: Record<LogLevel, string> = {
  debug: '🔍',
  info: 'ℹ️',
  log: '📝',
  warn: '⚠️',
  error: '❌',
};

/**
 * Text labels for each log level (non-emoji)
 */
const LOG_LEVEL_LABEL: Record<LogLevel, string> = {
  debug: '[DEBUG]',
  info: '[INFO]',
  log: '[LOG]',
  warn: '[WARN]',
  error: '[ERROR]',
};

/**
 * Format timestamp for logs
 */
function formatTimestamp(): string {
  const now = new Date();
  return now.toISOString().substring(11, 23); // HH:mm:ss.SSS
}

/**
 * Check if a log level should be displayed
 */
function shouldLog(config: LoggerConfig, level: LogLevel): boolean {
  if (!config.enabled) return false;
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[config.minLevel];
}

/**
 * Format the log prefix
 */
function formatPrefix(
  config: LoggerConfig,
  level: LogLevel,
  scope?: string,
): string {
  const parts: string[] = [];

  if (config.showTimestamp) {
    parts.push(`[${formatTimestamp()}]`);
  }

  // Use emoji or text label
  const useEmoji = config.useEmoji ?? config.isDev;
  parts.push(useEmoji ? LOG_LEVEL_EMOJI[level] : LOG_LEVEL_LABEL[level]);

  if (config.prefix) {
    parts.push(`[${config.prefix}]`);
  }

  if (scope) {
    parts.push(`[${scope}]`);
  }

  return parts.join(' ');
}

/**
 * Core logging function
 */
function logMessage(
  config: LoggerConfig,
  level: LogLevel,
  scope: string | undefined,
  args: unknown[],
): void {
  const prefix = formatPrefix(config, level, scope);

  // onLog is called unconditionally so in-app debug overlays receive all
  // messages regardless of the minLevel filter (which suppresses console
  // output in production but should not suppress the overlay).
  args = privateMediaTelemetry(args);
  config.onLog?.(level, prefix, args);

  if (!shouldLog(config, level)) return;

  // eslint-disable-next-line no-console
  const consoleFn = console[level] || console.log;

  if (prefix) {
    consoleFn(prefix, ...args);
  } else {
    consoleFn(...args);
  }
}

/**
 * Add Sentry breadcrumb if configured
 */
function addBreadcrumbIfConfigured(
  sentry: SentryIntegration | undefined,
  category: string,
  args: unknown[],
  level: 'debug' | 'info' | 'warning' | 'error',
): void {
  if (sentry?.isConfigured()) {
    sentry.addBreadcrumb({
      category,
      message: args.map((a) => String(a)).join(' '),
      level,
    });
  }
}

/**
 * Logger interface - full API
 */
export interface Logger {
  // Standard log levels
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  log: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void; // Only adds breadcrumb, does NOT send to Sentry
  error: (...args: unknown[]) => void; // Sends to Sentry

  // Expected errors (no Sentry capture, just breadcrumb)
  expectedError: (...args: unknown[]) => void;

  // Scoped logging
  scope: (name: string) => Logger;

  // Special loggers
  temp: (...args: unknown[]) => void;
  network: (...args: unknown[]) => void;
  api: (...args: unknown[]) => void;
  auth: (...args: unknown[]) => void;
  sync: (...args: unknown[]) => void;
  storage: (...args: unknown[]) => void;

  // Performance timing
  time: (label: string) => void;
  timeEnd: (label: string) => number | null;

  // Grouping
  group: (label: string) => void;
  groupCollapsed: (label: string) => void;
  groupEnd: () => void;

  // Table logging
  table: (data: unknown) => void;

  // Configuration
  configure: (newConfig: Partial<LoggerConfig>) => void;
  resetConfig: () => void;
  enable: () => void;
  disable: () => void;
  isDev: () => boolean;
}

/**
 * Create a logger instance with the given configuration
 */
export function createLogger(
  initialConfig: Partial<LoggerConfig> = {},
): Logger {
  const defaultConfig: LoggerConfig = {
    enabled: true,
    minLevel: initialConfig.isDev ? 'debug' : 'warn',
    showTimestamp: initialConfig.isDev ?? false,
    isDev: false,
    ...initialConfig,
  };

  let config: LoggerConfig = { ...defaultConfig };
  const timers = new Map<string, number>();

  /**
   * Create a scoped logger
   */
  function createScopedLogger(scopeName: string): Logger {
    // Scoped logger delegates to main logger but with fixed scope
    return {
      debug: (...args: unknown[]) => {
        logMessage(config, 'debug', scopeName, args);
        addBreadcrumbIfConfigured(config.sentry, 'debug', args, 'debug');
      },
      info: (...args: unknown[]) => {
        logMessage(config, 'info', scopeName, args);
        addBreadcrumbIfConfigured(config.sentry, 'info', args, 'info');
      },
      log: (...args: unknown[]) => logMessage(config, 'log', scopeName, args),
      warn: (...args: unknown[]) => {
        logMessage(config, 'warn', scopeName, args);
        // Warnings do NOT send to Sentry - only add breadcrumb for context
        addBreadcrumbIfConfigured(config.sentry, 'warning', args, 'warning');
      },
      error: (...args: unknown[]) => {
        logMessage(config, 'error', scopeName, args);
        if (config.sentry?.isConfigured()) {
          const firstArg = args[0];
          if (firstArg instanceof Error) {
            config.sentry.captureException(firstArg, {
              additionalArgs: args.slice(1),
            });
          } else {
            config.sentry.captureMessage(
              args.map((a) => String(a)).join(' '),
              'error',
            );
          }
        }
      },
      expectedError: (...args: unknown[]) => {
        logMessage(config, 'error', scopeName, args);
        addBreadcrumbIfConfigured(
          config.sentry,
          'expected-error',
          args,
          'error',
        );
      },
      scope: (subName: string) => createScopedLogger(`${scopeName}:${subName}`),
      temp: (...args: unknown[]) => {
        if (config.isDev) {
          // eslint-disable-next-line no-console
          console.log('🔧 [TEMP]', `[${scopeName}]`, ...args);
        }
      },
      network: (...args: unknown[]) =>
        logMessage(config, 'debug', `${scopeName}:Network`, args),
      api: (...args: unknown[]) =>
        logMessage(config, 'debug', `${scopeName}:API`, args),
      auth: (...args: unknown[]) =>
        logMessage(config, 'debug', `${scopeName}:Auth`, args),
      sync: (...args: unknown[]) =>
        logMessage(config, 'debug', `${scopeName}:Sync`, args),
      storage: (...args: unknown[]) =>
        logMessage(config, 'debug', `${scopeName}:Storage`, args),
      time: logger.time,
      timeEnd: logger.timeEnd,
      group: logger.group,
      groupCollapsed: logger.groupCollapsed,
      groupEnd: logger.groupEnd,
      table: logger.table,
      configure: logger.configure,
      resetConfig: logger.resetConfig,
      enable: logger.enable,
      disable: logger.disable,
      isDev: () => config.isDev,
    };
  }

  const logger: Logger = {
    // ===== Standard Log Levels =====
    debug: (...args: unknown[]) => {
      logMessage(config, 'debug', undefined, args);
      addBreadcrumbIfConfigured(config.sentry, 'debug', args, 'debug');
    },

    info: (...args: unknown[]) => {
      logMessage(config, 'info', undefined, args);
      addBreadcrumbIfConfigured(config.sentry, 'info', args, 'info');
    },

    log: (...args: unknown[]) => logMessage(config, 'log', undefined, args),

    warn: (...args: unknown[]) => {
      logMessage(config, 'warn', undefined, args);
      // Warnings do NOT send to Sentry - only add breadcrumb for context
      addBreadcrumbIfConfigured(config.sentry, 'warning', args, 'warning');
    },

    error: (...args: unknown[]) => {
      logMessage(config, 'error', undefined, args);
      if (config.sentry?.isConfigured()) {
        const firstArg = args[0];
        if (firstArg instanceof Error) {
          config.sentry.captureException(firstArg, {
            additionalArgs: args.slice(1),
          });
        } else {
          config.sentry.captureMessage(
            args.map((a) => String(a)).join(' '),
            'error',
          );
        }
      }
    },

    // ===== Expected Errors (no Sentry capture) =====
    expectedError: (...args: unknown[]) => {
      logMessage(config, 'error', undefined, args);
      addBreadcrumbIfConfigured(config.sentry, 'expected-error', args, 'error');
    },

    // ===== Scoped Logging =====
    scope: createScopedLogger,

    // ===== Special Loggers =====
    temp: (...args: unknown[]) => {
      if (config.isDev) {
        // eslint-disable-next-line no-console
        console.log('🔧 [TEMP]', ...args);
      }
    },

    network: (...args: unknown[]) =>
      logMessage(config, 'debug', 'Network', args),
    api: (...args: unknown[]) => logMessage(config, 'debug', 'API', args),
    auth: (...args: unknown[]) => logMessage(config, 'debug', 'Auth', args),
    sync: (...args: unknown[]) => logMessage(config, 'debug', 'Sync', args),
    storage: (...args: unknown[]) =>
      logMessage(config, 'debug', 'Storage', args),

    // ===== Performance Timing =====
    time: (label: string) => {
      if (!config.isDev) return;
      timers.set(label, performance.now());
    },

    timeEnd: (label: string): number | null => {
      if (!config.isDev) return null;

      const startTime = timers.get(label);
      if (startTime === undefined) {
        // eslint-disable-next-line no-console
        console.warn(`Timer "${label}" does not exist`);
        return null;
      }

      const duration = performance.now() - startTime;
      timers.delete(label);

      logMessage(config, 'debug', 'Timer', [
        `${label}: ${duration.toFixed(2)}ms`,
      ]);
      return duration;
    },

    // ===== Grouping =====
    group: (label: string) => {
      if (config.isDev && console.group) {
        // eslint-disable-next-line no-console
        console.group(label);
      }
    },

    groupCollapsed: (label: string) => {
      if (config.isDev && console.groupCollapsed) {
        // eslint-disable-next-line no-console
        console.groupCollapsed(label);
      }
    },

    groupEnd: () => {
      if (config.isDev && console.groupEnd) {
        // eslint-disable-next-line no-console
        console.groupEnd();
      }
    },

    // ===== Table Logging =====
    table: (data: unknown) => {
      if (config.isDev && console.table) {
        // eslint-disable-next-line no-console
        console.table(data);
      }
    },

    // ===== Configuration =====
    configure: (newConfig: Partial<LoggerConfig>) => {
      config = { ...config, ...newConfig };
    },

    resetConfig: () => {
      config = { ...defaultConfig };
    },

    enable: () => {
      config.enabled = true;
    },

    disable: () => {
      config.enabled = false;
    },

    isDev: () => config.isDev,
  };

  return logger;
}
